import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { scoreColor, TIER_LABELS, fmtDate } from "@/lib/format";
import {
  Watch, Crown, Search, Tag, CheckSquare, ShieldAlert, Star, ShoppingBag,
} from "lucide-react";

function Stat({ icon: Icon, label, value, to, accent }) {
  const inner = (
    <Card className="p-5 hover:shadow-md transition-shadow border-[var(--border-lch)]">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-3xl font-semibold text-[var(--text)]">{value ?? 0}</div>
          <div className="text-xs text-[var(--text-2)] mt-1">{label}</div>
        </div>
        <div className={`p-2.5 rounded-lg ${accent || "bg-[var(--panel-2)] text-[#c9a86a]"}`}>
          <Icon size={20} />
        </div>
      </div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function AdminDashboard() {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[var(--text)]">Good day, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-[var(--text-2)]">Here is the state of the house.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Stat icon={Crown} label="New VIP Leads" value={stats?.vip_leads_new} to="/admin/leads" accent="bg-amber-50 text-amber-700" />
        <Stat icon={Search} label="Open Watch Requests" value={stats?.watch_requests_open ?? stats?.watch_requests_new} to="/admin/requests" accent="bg-blue-50 text-blue-700" />
        <Stat icon={Star} label="Hot Requests (score≥70)" value={stats?.watch_requests_hot} to="/admin/requests" accent="bg-rose-50 text-rose-700" />
        <Stat icon={Tag} label="New Seller Intakes" value={stats?.seller_intakes_new} to="/admin/sellers" accent="bg-purple-50 text-purple-700" />
        <Stat icon={CheckSquare} label="Open Tasks" value={stats?.tasks_open} to="/admin/tasks" accent="bg-emerald-50 text-emerald-700" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Watch} label="Watches in Inventory" value={stats?.watches_total} to="/admin/watches" />
        <Stat icon={Star} label="Available" value={stats?.watches_available} to="/admin/watches" accent="bg-emerald-50 text-emerald-700" />
        <Stat icon={ShieldAlert} label="In Authentication" value={stats?.watches_auth_pending} to="/admin/watches" accent="bg-purple-50 text-purple-700" />
        <Stat icon={ShoppingBag} label="Merch Orders" value={stats?.orders_total} to="/admin/orders" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5 border-[var(--border-lch)]">
          <h2 className="font-semibold mb-3 text-[var(--text)]">Latest VIP Leads</h2>
          <div className="space-y-2">
            {(stats?.recent_leads || []).map((l) => (
              <Link to="/admin/leads" key={l.id} className="flex items-center justify-between py-2 border-b border-[var(--border-lch)] last:border-0">
                <div>
                  <div className="text-sm font-medium">{l.name}</div>
                  <div className="text-xs text-[var(--text-2)]">{TIER_LABELS[l.tier] || l.tier} · {fmtDate(l.created_at)}</div>
                </div>
                <Badge variant="outline" className={scoreColor(l.lead_score)}>{l.lead_score}</Badge>
              </Link>
            ))}
            {!stats?.recent_leads?.length && <p className="text-sm text-[var(--muted)]">No leads yet.</p>}
          </div>
        </Card>
        <Card className="p-5 border-[var(--border-lch)]">
          <h2 className="font-semibold mb-3 text-[var(--text)]">Open Follow-up Tasks</h2>
          <div className="space-y-2">
            {(stats?.recent_tasks || []).map((t) => (
              <Link to="/admin/tasks" key={t.id} className="flex items-center justify-between py-2 border-b border-[var(--border-lch)] last:border-0">
                <div className="text-sm">{t.title}</div>
                <Badge variant="outline" className="capitalize">{t.priority}</Badge>
              </Link>
            ))}
            {!stats?.recent_tasks?.length && <p className="text-sm text-[var(--muted)]">No open tasks.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
